import { useState } from "react";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { QuoteTerm } from "../../shared/types";
import { EditorSection, EditorSectionDates } from "../QuoteSectionBar/sectionUtils";
import { formatIsoDate, isOutsideCurrentPricingMonth, pastFirstTermWarning } from "../../shared/quoteDate";
import QuoteDatesModal from "./QuoteDatesModal";

type Props = {
  section: EditorSection;
  sectionLabel: string;
  editable: boolean;
  quoteType: "customer" | "prospect";
  customerGuid: string | null;
  pricingMonth?: string | null;
  onChange: (dates: EditorSectionDates) => void;
};

export const sectionDatesSummary = (dates: EditorSectionDates): string => {
  if (!dates.startDate) return "Not set - the standard subscription term from today";
  const end = dates.endDateSource === "coterm" ? ` - Co-term end: ${formatIsoDate(dates.endDate)}`
    : dates.endDateSource === "manual" || dates.endDate ? ` - End date: ${formatIsoDate(dates.endDate)}`
    : " - Standard term";
  return `Start date: ${formatIsoDate(dates.startDate)}${end}`;
};

const QuoteSectionDatesRow = ({ section, sectionLabel, editable, quoteType, customerGuid, pricingMonth, onChange }: Props) => {
  const [open, setOpen] = useState(false);
  const term = (section.items.find(item => item.term_duration)?.term_duration || "P1YA") as QuoteTerm;
  const sectionTerms = section.items.map(item => item.term_duration).filter(Boolean) as QuoteTerm[];
  const terms = sectionTerms.length ? sectionTerms : [term];
  const speculative = isOutsideCurrentPricingMonth(section.dates.startDate, pricingMonth);
  const firstTermWarning = pastFirstTermWarning(section.dates.startDate, section.dates.endDate, section.dates.endDateSource, terms);

  return <Box className="quote-section-dates-row" sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", my: 1 }}>
    <EventOutlinedIcon fontSize="small" color="action" />
    <Typography variant="body2" color="text.secondary">{sectionDatesSummary(section.dates)}</Typography>
    {speculative && <Tooltip title="This estimate uses current Microsoft pricing. Actual pricing may change before the selected start date.">
      <Chip size="small" color="warning" icon={<WarningAmberRoundedIcon />} label="Speculative pricing" />
    </Tooltip>}
    {firstTermWarning && <Tooltip title={firstTermWarning}>
      <Chip size="small" color="warning" icon={<WarningAmberRoundedIcon />} label="Past first term" />
    </Tooltip>}
    {editable && <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
      {section.dates.startDate ? "Change Dates" : "Set Dates"}
    </Button>}
    {open && <QuoteDatesModal open onClose={() => setOpen(false)} scope="section" quoteType={quoteType}
      customerGuid={customerGuid} term={term} terms={terms} title={`Dates for ${sectionLabel}`} value={section.dates}
      estimatedCurrentTotal={section.items.reduce((sum, item) => sum + (item.total_price ?? 0), 0)}
      onConfirm={result => { onChange(result.dates); setOpen(false); }} />}
  </Box>;
};

export default QuoteSectionDatesRow;
